import { useState, useMemo, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { SocialMedia } from '../utils/api';
import { BarChart } from '../components/Charts';
import { COLORS, fmtNum, fmtPct } from '../utils/theme';

// The channel that matches a brand's own name (self-overlap), if any.
function selfChannelFor(name, channels) {
  const n = String(name || '').toLowerCase();
  return channels.find(c => c.toLowerCase() === n) || null;
}

// Media-affinity KPI cards (Panel Size / Football style): national audience's
// affinity to the top social channels. Uses penetration vs base when available,
// otherwise national allocation share.
function AffinityCards({ nationalRow, channels, title, channelSub }) {
  if (!nationalRow || !channels.length) return null;
  const base = nationalRow.active_users_x;
  const alloc = allocation(nationalRow.cells, channels);
  const top = channels.slice(0, 4);
  return (
    <>
      {title && <div className="sm-subhead" style={{ marginTop: 0 }}>{title}</div>}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        {base != null && (
          <div className="kpi-card" style={{ background: COLORS.teal, borderColor: COLORS.teal }}>
            <div className="kpi-label" style={{ color: 'rgba(255,255,255,0.85)' }}>All Mobile Users</div>
            <div className="kpi-value" style={{ color: '#fff' }}>{fmtNum(base)}</div>
            <div className="kpi-delta neutral" style={{ color: 'rgba(255,255,255,0.75)' }}>ALL Universe</div>
          </div>
        )}
        {top.map(ch => {
          const reach = nationalRow.cells[ch] || 0;
          const pen   = base ? (reach / base) * 100 : alloc[ch];
          return (
            <div className="kpi-card" key={ch}>
              <div className="kpi-label">{ch}</div>
              <div className="kpi-value">{fmtPct(pen)}</div>
              <div className="kpi-delta neutral">
                {base ? `${fmtNum(reach)} reached · national` : (channelSub || 'national spread')}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// Allocation % across channels for a row (channel reach ÷ row total)
function allocation(cells, channels) {
  const total = channels.reduce((s, ch) => s + (cells[ch] || 0), 0);
  const out = {};
  channels.forEach(ch => { out[ch] = total ? ((cells[ch] || 0) / total) * 100 : 0; });
  return out;
}

// Index-vs-national → background colour (red <100, green >100, graded)
function indexColor(idx) {
  if (idx == null || !isFinite(idx)) return 'transparent';
  const diff = idx - 100;
  const a = Math.min(Math.abs(diff) / 80, 1) * 0.5;
  if (diff >= 0) return `rgba(29,184,154,${a.toFixed(3)})`;   // teal/green
  return `rgba(229,72,77,${a.toFixed(3)})`;                    // red
}

// ── Shared pivot ────────────────────────────────────────────────────────────
// excludeSelf: blank the diagonal self-cell (brand that is also a channel) and
// re-base that row's % across the remaining channels.
function PivotTable({ leftCols, nationalRow, rows, channels, metric, excludeSelf = false, onRowClick, selectedId }) {
  const [sort, setSort] = useState({ ch: null, dir: 'desc' });   // channel column sort
  const nat = nationalRow ? allocation(nationalRow.cells, channels) : null;

  // Per-row allocation, optionally excluding the brand's own channel
  const rowInfo = (row) => {
    const selfCh = excludeSelf ? selfChannelFor(row.left.brand, channels) : null;
    const chs = selfCh ? channels.filter(c => c !== selfCh) : channels;
    return { alloc: allocation(row.cells, chs), selfCh };
  };

  // Column sort — orders body rows by the clicked channel's displayed value.
  // The national row is rendered separately, so it stays pinned at the top.
  const onSortClick = ch => setSort(s =>
    s.ch === ch ? { ch, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { ch, dir: 'desc' });
  const dispVal = (row, ch) => {
    const { alloc, selfCh } = rowInfo(row);
    if (selfCh && ch === selfCh) return -Infinity;
    return metric === 'index' ? (nat && nat[ch] ? (alloc[ch] / nat[ch]) * 100 : 0) : (alloc[ch] || 0);
  };
  const sortedRows = sort.ch
    ? [...rows].sort((a, b) => sort.dir === 'desc'
        ? dispVal(b, sort.ch) - dispVal(a, sort.ch)
        : dispVal(a, sort.ch) - dispVal(b, sort.ch))
    : rows;

  const renderCell = (row, ch, alloc, selfCh) => {
    if (selfCh && ch === selfCh) return <td key={ch} className="fa-num sm-self">—</td>;
    if (metric === 'index') {
      const idx = nat && nat[ch] ? (alloc[ch] / nat[ch]) * 100 : null;
      return (
        <td key={ch} className="fa-num" style={{ background: indexColor(idx) }}>
          {idx != null ? Math.round(idx) : '—'}
        </td>
      );
    }
    return <td key={ch} className="fa-num">{alloc[ch] ? alloc[ch].toFixed(1) + '%' : '—'}</td>;
  };

  // Fixed-width sticky columns tiled by cumulative offset (prevents overlap/gaps)
  const stickyStyle = (c, isLast) => ({
    left: c.left,
    width: c.width,
    minWidth: c.width,
    maxWidth: c.width,
    ...(isLast ? { boxShadow: '2px 0 0 var(--border-mid)' } : {}),
  });

  return (
    <div className="fa-pivot-scroll sm-pivot-scroll">
      <table className="data-table fa-pivot sm-pivot">
        <thead>
          <tr>
            {leftCols.map((c, i) => (
              <th key={c.key} className="fa-sticky-col sm-sticky" style={stickyStyle(c, i === leftCols.length - 1)}>
                {c.label}
              </th>
            ))}
            {channels.map(ch => (
              <th
                key={ch}
                className={`fa-num fa-sortable ${sort.ch === ch ? 'fa-sorted' : ''}`}
                onClick={() => onSortClick(ch)}
                title="Click to sort"
              >
                {ch}{sort.ch === ch ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {nationalRow && (
            <tr className="fa-group-row sm-national">
              {leftCols.map((c, i) => (
                <td key={c.key} className="fa-sticky-col sm-sticky" style={stickyStyle(c, i === leftCols.length - 1)}>
                  {i === 0 ? 'National distribution' : ''}
                </td>
              ))}
              {channels.map(ch => (
                <td key={ch} className="fa-num">{nat[ch] ? nat[ch].toFixed(1) + '%' : '—'}</td>
              ))}
            </tr>
          )}
          {sortedRows.map(row => {
            const { alloc, selfCh } = rowInfo(row);
            return (
              <tr
                key={row.id}
                className={(onRowClick ? 'sm-clickable ' : '') + (selectedId === row.id ? 'fa-active-row' : '')}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {leftCols.map((c, i) => (
                  <td
                    key={c.key}
                    className="fa-sticky-col sm-sticky sm-left"
                    style={stickyStyle(c, i === leftCols.length - 1)}
                    title={row.left[c.key] ?? ''}
                  >
                    {row.left[c.key] ?? '—'}
                  </td>
                ))}
                {channels.map(ch => renderCell(row, ch, alloc, selfCh))}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td className="fa-sticky-col" colSpan={leftCols.length + channels.length}
                    style={{ color: 'var(--text-muted)' }}>No rows match the current filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Filter({ label, value, onChange, options, allLabel = 'All' }) {
  return (
    <>
      <span className="filter-label">{label}</span>
      <select className="filter-select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">{allLabel}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </>
  );
}

function MetricToggle({ metric, setMetric }) {
  return (
    <div className="dj-metric-toggle">
      <button className={`dj-metric-btn ${metric === 'abs' ? 'active' : ''}`} onClick={() => setMetric('abs')}>
        Absolute %
      </button>
      <button className={`dj-metric-btn ${metric === 'index' ? 'active' : ''}`} onClick={() => setMetric('index')}>
        Index vs National
      </button>
    </div>
  );
}

// ── TAB 1: Brand Behavior ────────────────────────────────────────────────────
function BrandTab() {
  const { data, loading, error } = useApi(() => SocialMedia.brand(''), {}, true);
  const [metric, setMetric] = useState('abs');
  const [macro,  setMacro]  = useState('');
  const [micro,  setMicro]  = useState('');
  const [search, setSearch] = useState('');
  const [selectedAppId, setSelectedAppId] = useState(null);
  const chartRef = useRef(null);
  const selectRow = r => {
    setSelectedAppId(r.id);
    requestAnimationFrame(() => chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const nationalRow = useMemo(
    () => (data || []).find(r => r.application === 'ALL Universe') || null,
    [data],
  );

  const channels = useMemo(() => {
    if (!nationalRow) return [];
    return Object.keys(nationalRow.cells)
      .sort((a, b) => (nationalRow.cells[b] || 0) - (nationalRow.cells[a] || 0));
  }, [nationalRow]);

  // KPI cards keep the original user-reach values (active_users_xy), independent
  // of the session-based pivot table. Uses user cells + user-based channel order.
  const cardRow = useMemo(
    () => nationalRow ? { ...nationalRow, cells: nationalRow.cells_users || nationalRow.cells } : null,
    [nationalRow],
  );
  const cardChannels = useMemo(() => {
    const c = cardRow?.cells || {};
    return Object.keys(c).sort((a, b) => (c[b] || 0) - (c[a] || 0));
  }, [cardRow]);

  const macros = useMemo(
    () => [...new Set((data || []).map(r => r.macro_category).filter(Boolean))].sort(),
    [data],
  );
  const micros = useMemo(
    () => [...new Set((data || [])
      .filter(r => !macro || r.macro_category === macro)
      .map(r => r.micro_category).filter(Boolean))].sort(),
    [data, macro],
  );

  // Media apps that are also channel columns (YouTube, Facebook, …) are excluded
  // as application_x rows — they already appear as columns, so there is no self-
  // overlap cell to re-allocate.
  const channelSet = useMemo(() => new Set(channels.map(c => c.toLowerCase())), [channels]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data || [])
      .filter(r => r.application !== 'ALL Universe')
      .filter(r => !channelSet.has(r.application.toLowerCase()))
      .filter(r => !macro  || r.macro_category === macro)
      .filter(r => !micro  || r.micro_category === micro)
      .filter(r => !q      || r.application.toLowerCase().includes(q))
      .sort((a, b) => (b.active_users_x || 0) - (a.active_users_x || 0))
      .map(r => ({
        id: r.application,
        left: { brand: r.application, macro: r.macro_category, micro: r.micro_category },
        cells: r.cells,
      }));
  }, [data, macro, micro, search, channelSet]);

  const leftCols = [
    { key: 'brand', label: 'Brand',           left: 0,   width: 170 },
    { key: 'macro', label: 'Macro Category',  left: 170, width: 150 },
    { key: 'micro', label: 'Micro Category',  left: 320, width: 200 },
  ];

  // National-vs-Brand comparison for the clicked app (self-channel excluded, re-based)
  const pieRow = useMemo(
    () => rows.find(r => r.id === selectedAppId) || rows[0] || null,
    [rows, selectedAppId],
  );
  const compare = useMemo(() => {
    if (!nationalRow || !pieRow || !channels.length) return null;
    const selfCh = selfChannelFor(pieRow.left.brand, channels);
    const chs = selfCh ? channels.filter(c => c !== selfCh) : channels;
    const nat = allocation(nationalRow.cells, chs);
    const brand = allocation(pieRow.cells, chs);
    return {
      name: pieRow.left.brand,
      categories: chs,
      natData: chs.map(c => +(nat[c] || 0).toFixed(2)),
      brandData: chs.map(c => +(brand[c] || 0).toFixed(2)),
    };
  }, [nationalRow, pieRow, channels]);

  if (loading) return <div className="loading-state"><div className="spinner" /> Fetching data…</div>;
  if (error)   return <div className="empty-state" style={{ color: 'var(--negative)' }}>Error: {error}</div>;

  return (
    <>
      <AffinityCards nationalRow={cardRow} channels={cardChannels} />
      <div className="filters-bar">
        <Filter label="Macro" value={macro} onChange={v => { setMacro(v); setMicro(''); }} options={macros} />
        <Filter label="Micro" value={micro} onChange={setMicro} options={micros} />
        <span className="filter-label">Brand</span>
        <input className="filter-select" placeholder="Search brand…" value={search}
               onChange={e => setSearch(e.target.value)} style={{ cursor: 'text' }} />
        <div style={{ flex: 1 }} />
        <MetricToggle metric={metric} setMetric={setMetric} />
      </div>

      <div className="sm-subhead">
        Media apps shown as channels only (excluded as brand rows) · click a row to chart it
      </div>
      <PivotTable
        leftCols={leftCols} nationalRow={nationalRow} rows={rows} channels={channels} metric={metric}
        onRowClick={selectRow} selectedId={pieRow?.id}
      />

      {compare && (
        <>
          <div className="sm-subhead" ref={chartRef}>
            National vs <strong>{compare.name}</strong> — channel distribution (%)
          </div>
          <div className="dj-chart-card">
            <BarChart
              categories={compare.categories}
              series={[
                { name: 'National', data: compare.natData, color: COLORS.textMuted || '#9CA3AF' },
                { name: compare.name, data: compare.brandData, color: COLORS.teal },
              ]}
              horizontal
              height={Math.max(360, compare.categories.length * 26)}
              xFormatter={v => `${v.toFixed(0)}%`}
              tooltipFormatter={v => `${(+v).toFixed(1)}%`}
            />
          </div>
        </>
      )}
    </>
  );
}

// ── TAB 2: Category Frequency ────────────────────────────────────────────────
function IntensityTab() {
  const [sessionType, setSessionType] = useState('open_app');
  const [intensity,   setIntensity]   = useState('Heavy');
  const [metric, setMetric] = useState('abs');
  const [macro,  setMacro]  = useState('');
  const [micro,  setMicro]  = useState('');
  const [selectedCatId, setSelectedCatId] = useState(null);
  const chartRef = useRef(null);
  const selectRow = r => {
    setSelectedCatId(r.id);
    requestAnimationFrame(() => chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const filters = useApi(() => SocialMedia.intensityFilters(), {}, true);
  const { data, loading, error } = useApi(
    () => SocialMedia.intensity('', sessionType),
    { sessionType },
    true,
  );

  const opts = filters.data?.[0] || { session_types: [], intensities: [] };

  const nationalRow = useMemo(
    () => (data || []).find(r => r.is_national && r.intensity === intensity) || null,
    [data, intensity],
  );

  const channels = useMemo(() => {
    if (!nationalRow) return [];
    return Object.keys(nationalRow.cells)
      .sort((a, b) => (nationalRow.cells[b] || 0) - (nationalRow.cells[a] || 0));
  }, [nationalRow]);

  const macros = useMemo(
    () => [...new Set((data || []).filter(r => !r.is_national).map(r => r.macro_category).filter(Boolean))].sort(),
    [data],
  );
  const micros = useMemo(
    () => [...new Set((data || [])
      .filter(r => !r.is_national && (!macro || r.macro_category === macro))
      .map(r => r.category).filter(Boolean))].sort(),
    [data, macro],
  );

  const rows = useMemo(() => (data || [])
    .filter(r => !r.is_national)
    .filter(r => r.intensity === intensity)
    .filter(r => !macro || r.macro_category === macro)
    .filter(r => !micro || r.category === micro)
    .sort((a, b) => (b.active_users_x || 0) - (a.active_users_x || 0))
    .map(r => ({
      id: r.category + '|' + r.intensity,
      left: { macro: r.macro_category, micro: r.category, intensity: r.intensity },
      cells: r.cells,
    })), [data, intensity, macro, micro]);

  const leftCols = [
    { key: 'micro',     label: 'Micro Category', left: 0,   width: 210 },
    { key: 'macro',     label: 'Macro Category', left: 210, width: 150 },
    { key: 'intensity', label: 'Intensity',      left: 360, width: 90  },
  ];

  // Selected category row → National-vs-Category comparison bar chart
  const selRow = useMemo(
    () => rows.find(r => r.id === selectedCatId) || rows[0] || null,
    [rows, selectedCatId],
  );
  const compare = useMemo(() => {
    if (!nationalRow || !selRow || !channels.length) return null;
    const nat = allocation(nationalRow.cells, channels);
    const cat = allocation(selRow.cells, channels);
    return {
      name: selRow.left.micro,
      categories: channels,
      natData: channels.map(c => +(nat[c] || 0).toFixed(2)),
      catData: channels.map(c => +(cat[c] || 0).toFixed(2)),
    };
  }, [nationalRow, selRow, channels]);

  return (
    <>
      <AffinityCards
        nationalRow={nationalRow} channels={channels}
        title={`National social channel mix — ${intensity} intensity · ${sessionType} (top 4 channels)`}
        channelSub="national share of channels"
      />
      <div className="filters-bar">
        <span className="filter-label">Funnel stage</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {opts.session_types.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="filter-label">Intensity</span>
        <select className="filter-select" value={intensity} onChange={e => setIntensity(e.target.value)}>
          {opts.intensities.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Filter label="Macro" value={macro} onChange={v => { setMacro(v); setMicro(''); }} options={macros} />
        <Filter label="Micro" value={micro} onChange={setMicro} options={micros} />
        <div style={{ flex: 1 }} />
        <MetricToggle metric={metric} setMetric={setMetric} />
      </div>
      {loading && <div className="loading-state"><div className="spinner" /> Fetching data…</div>}
      {error && !loading && <div className="empty-state" style={{ color: 'var(--negative)' }}>Error: {error}</div>}
      {!loading && !error && (
        <>
          <div className="sm-subhead">
            {intensity} intensity · {sessionType} · click a category row to compare it below
          </div>
          <PivotTable
            leftCols={leftCols} nationalRow={nationalRow} rows={rows} channels={channels} metric={metric}
            onRowClick={selectRow} selectedId={selRow?.id}
          />

          {compare && (
            <>
              <div className="sm-subhead" ref={chartRef}>
                National vs <strong>{compare.name}</strong> — channel distribution (%)
              </div>
              <div className="dj-chart-card">
                <BarChart
                  categories={compare.categories}
                  series={[
                    { name: 'National', data: compare.natData, color: COLORS.textMuted || '#9CA3AF' },
                    { name: compare.name, data: compare.catData, color: COLORS.teal },
                  ]}
                  horizontal
                  height={Math.max(360, compare.categories.length * 26)}
                  xFormatter={v => `${v.toFixed(0)}%`}
                  tooltipFormatter={v => `${(+v).toFixed(1)}%`}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

// Rendered once per sidebar item under the "Media Budget Allocation" section.
// Title lives in the topbar (main header), so no in-card header here.
export default function MediaBudgetView({ mode = 'brand' }) {
  return (
    <div className="sm-view">
      <div className="dj-section sm-content">
        {mode === 'brand' ? <BrandTab /> : <IntensityTab />}
      </div>
    </div>
  );
}
